/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, IconButton, MenuItem, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";

import ChipMenuDialog from "../../../components/ChipMenuDialog/ChipMenuDialog";
import MaterialSymbol from "../../../components/MaterialSymbol";
import { SeedMix } from "../types/SeedOrderFormType";

import { customName, premixName } from "./NewSeedTypeButton";
import { useSeedEditPriceModal } from "./useRenameSeedMixModal";

export const MenuEntry = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <MenuItem onClick={onClick}>
    <Typography variant="body2">{label}</Typography>
  </MenuItem>
);

export const SeedMixMenu = ({
  onAddPremix,
  onAddCustom,
  onCopy,
  onRemove,
  seedMix,
}: {
  onAddPremix: () => void;
  onAddCustom: () => void;
  onCopy: () => void;
  onRemove: () => void;
  seedMix: SeedMix;
}) => {
  const theme = useTheme();

  const { setRenamerOpen, setSeedMix } = useSeedEditPriceModal();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const rename = () => {
    setSeedMix(seedMix);
    setRenamerOpen(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const copy = () => {
    setOpen(false);
    onCopy();
  };
  const remove = () => {
    setOpen(false);
    onRemove();
  };
  const addPremix = () => {
    setOpen(false);
    onAddPremix();
  };
  const addCustom = () => {
    setOpen(false);
    onAddCustom();
  };

  return (
    <>
      <IconButton aria-label="Seed Mix Menu" color="primary" onClick={handleClick} sx={{ p: 0 }}>
        <MaterialSymbol>more_vert</MaterialSymbol>
      </IconButton>
      <ChipMenuDialog
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        paperSx={{ padding: 0 }}
      >
        <MenuEntry onClick={rename} label="Rename" />
        <MenuEntry onClick={copy} label="Duplicate" />
        <MenuEntry onClick={remove} label="Remove" />
        <Box bgcolor={theme.colors.primary50}>
          <Typography
            variant="body2"
            fontWeight="700"
            color={theme.colors.primary500}
            paddingX="16px"
            paddingY="8px"
          >
            Create a new order
          </Typography>
          <MenuEntry onClick={addPremix} label={`${premixName} order`} />
          <MenuEntry onClick={addCustom} label={`${customName} order`} />
        </Box>
      </ChipMenuDialog>
    </>
  );
};
